//
// AHT Weight Management — Shared Assessment (Phase 1 Gate, Initial Consultations Only)
// Source: AHT Shared Assessment
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Drug-specific exclusions are NOT assessed here.

import type { Pathway, IOTFLookupNode } from "../../types/pathway.types";
import { mapEthnicityToAdjustmentGroup } from "./weight-selection.pathway";

const ADJUSTED_GROUPS = [
  "south_asian", "chinese", "other_asian",
  "middle_eastern", "black_african", "african_caribbean",
];

function resolveEthnicGroup(patient: any): string | undefined {
  const raw = patient?.ethnicAdjustmentGroup;
  const val = typeof raw === "object" && raw !== null ? raw.value : raw;
  if (!val) return undefined;
  if (ADJUSTED_GROUPS.includes(val)) return val;
  return mapEthnicityToAdjustmentGroup(val) ?? undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const SA_Q0_GATEWAY              = "sa-q0-gateway";

// Inclusion checks
const SA_Q1_AGE                      = "sa-q1-age";
const SA_Q2_BMI_CAPTURE              = "sa-q2-bmi-capture";
const SA_Q3_CONSENT                  = "sa-q3-consent";      // entry point: commitments checklist
const SA_Q3_COMMITMENTS_CHECKLIST    = "sa-q3-commitments-checklist";

// Exclusion checks
const SA_Q7_BMI_FLOOR                = "sa-q7-bmi-floor";    // entry point: exclusions checklist
const SA_Q7_EXCLUSIONS_CHECKLIST     = "sa-q7-exclusions-checklist";

// Red flags alarm node
const SA_RED_FLAGS_ALARM      = "sa-red-flags-alarm";

// Adolescent branch
const SA_Q1A_WEIGHT           = "sa-q1a-weight";
const SA_Q1A_PARENTAL         = "sa-q1a-parental";
const SA_Q2_BMI_CONFIRM              = "sa-q2-bmi-confirm";
const SA_Q2_BMI_CONFIRM_MANUAL       = "sa-q2-bmi-confirm-manual";
const SA_Q2_BMI_ADOLESCENT           = "sa-q2-bmi-adolescent";
const SA_Q2_BMI_INCL_ADOLESCENT_NEXT = "sa-q2-bmi-incl-adolescent-next";

// Outcomes
const OUT_INCLUSION_NOT_MET   = "OUT_INCLUSION_NOT_MET";
const OUT_EXCLUSION_TRIGGERED = "OUT_EXCLUSION_TRIGGERED";
const OUT_CLEARED             = "OUT_CLEARED";
const OUT_UNDER12             = "OUT_UNDER12";
const OUT_OVER84              = "OUT_OVER84";
const OUT_BMI_BELOW_THRESHOLD = "OUT_BMI_BELOW_THRESHOLD";
const OUT_BMI_IOTF_BELOW      = "OUT_BMI_IOTF_BELOW";
const OUT_WEIGHT_BELOW_60KG   = "OUT_WEIGHT_BELOW_60KG";
const OUT_CONSENT_REFUSED     = "OUT_CONSENT_REFUSED";
const OUT_MONITORING_REFUSED  = "OUT_MONITORING_REFUSED";
const OUT_LIFESTYLE_DECLINED       = "OUT_LIFESTYLE_DECLINED";
const OUT_COMMITMENTS_NOT_MET      = "OUT_COMMITMENTS_NOT_MET";

// Re-initiation branch (returning patients — replaces the Q2 BMI gate)
const SA_REINIT_ROUTE       = "sa-reinit-consultation-type-route";
const SA_REINIT_Q1          = "sa-reinit-q1-original-bmi";
const SA_REINIT_Q2          = "sa-reinit-q2-weight-loss-response";
const SA_REINIT_Q3          = "sa-reinit-q3-within-12-months";
const SA_REINIT_Q4          = "sa-reinit-q4-weight-regain";
const SA_REINIT_BMI_FLOOR   = "sa-reinit-bmi-floor-check";
const OUT_REINIT_CANNOT     = "OUT_REINIT_CANNOT_PROCEED";
const OUT_REINIT_OVER12M    = "OUT_REINIT_OVER_12_MONTHS";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtSharedAssessmentPathway: Pathway = {
  id: "aht-wt-shared-assessment",
  name: "AHT Weight Management — Shared Assessment",
  population: "Adults presenting for initial weight management consultation",
  pgdRefs: [],
  demographicConfiguration: { bannerFields: [] },
  entryNode: SA_Q0_GATEWAY,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────────
    // Step 1 — Red flags checklist
    // ─────────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q0_GATEWAY,
      type: "checklist",
      text: "Check for red flags — select all that apply",
      hint: "Select any red flags present. If none apply, select 'None of the above' and continue.",
      items: [
        {
          id: "rf-systemically-unwell",
          text: "Patient appears systemically unwell — extreme confusion, mottled or discoloured skin, extreme pain, no urination, extreme temperature",
        },
        {
          id: "rf-life-threatening",
          text: "Life-threatening presentation — call 999 immediately",
        },
        {
          id: "rf-anaphylaxis",
          text: "Signs of anaphylaxis or severe allergic reaction — urticaria, angioedema, bronchospasm, hypotension",
        },
        {
          id: "rf-pancreatitis",
          text: "Suspected pancreatitis — severe persistent abdominal pain, fever, nausea, vomiting, jaundice",
        },
        {
          id: "rf-pregnancy",
          text: "Pregnancy confirmed or suspected",
        },
        {
          id: "rf-suicidal-ideation",
          text: "Active suicidal ideation, recent self-harm, or recent suicide attempt",
        },
        {
          id: "rf-hypertensive-crisis",
          text: "Hypertensive crisis — sudden severe headache, chest pain, visual disturbance, very high BP",
        },
        {
          id: "rf-acute-illness",
          text: "Acute illness making initiation of weight management treatment inappropriate at this time",
        },
      ],
      includeNoneOption: true,
      anySelected: SA_RED_FLAGS_ALARM,
      noneSelected: SA_Q1_AGE,
    },

    // Red flags alarm — terminates consultation, offers NEWS2 calculator
    {
      id: SA_RED_FLAGS_ALARM,
      type: "gateway_alarm",
      heading: "Red flag present — do not proceed with weight management consultation",
      body:
        "One or more red flags have been identified. Do not initiate or continue a weight management consultation at this time.\n\n" +
        "If the patient is systemically unwell, calculate NEWS2 and refer urgently to an appropriate setting.\n" +
        "If the presentation is life-threatening, call 999 immediately.\n" +
        "Suspected pancreatitis: do not supply any weight management medicine — refer urgently to A&E.\n" +
        "Confirmed or suspected pregnancy: do not supply — all five weight management medicines are contraindicated.\n" +
        "Active suicidal ideation: refer urgently to GP or mental health crisis team. Do not supply Mysimba under any circumstances.\n" +
        "Hypertensive crisis: do not supply Mysimba — refer urgently. Reassess other medicines once BP is controlled.\n" +
        "Document the red flag identified and the action taken.",
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score",
      action: "close",
      triggersPayment: false,
      confirmBeforeClose: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2 — Shared inclusion criteria (all must be Yes)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q1_AGE,
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 12 },                             then: { outcomeId: OUT_UNDER12 } },
        { when: { patientAgeAtLeast: 12, patientAgeUnder: 18 },      then: SA_Q1A_WEIGHT },
        { when: { patientAgeAtLeast: 18, patientAgeUnder: 85 },      then: SA_REINIT_ROUTE },
        { when: { patientAgeAtLeast: 85 },                           then: { outcomeId: OUT_OVER84 } },
      ],
      default: { outcomeId: OUT_INCLUSION_NOT_MET },
    },

    // Adolescent branch — weight check (>60 kg required for liraglutide)
    {
      id: SA_Q1A_WEIGHT,
      type: "yes_no",
      text: "Does the patient weigh more than 60 kg?",
      hint: "Liraglutide in adolescents (12–17) requires body weight above 60 kg. If below 60 kg, liraglutide is not eligible under this PGD.",
      storesTo: "sharedAdolescentWeightEligible",
      yes: SA_Q2_BMI_CAPTURE,
      no: { outcomeId: OUT_WEIGHT_BELOW_60KG },
    },

    // Adolescent BMI — IOTF lookup
    {
      id: SA_Q2_BMI_ADOLESCENT,
      type: "iotf_lookup",
      iotfTableId: "iotf-cutoffs-v1",
      meetsThreshold: SA_Q1A_PARENTAL,
      belowThreshold: { outcomeId: OUT_BMI_IOTF_BELOW },
      pendingDataOutcome: { outcomeId: OUT_BMI_IOTF_BELOW },
      sexNotResolvable: { outcomeId: OUT_BMI_IOTF_BELOW },
    } as IOTFLookupNode,

    // Adolescent parental involvement — soft gate, not a hard block
    {
      id: SA_Q1A_PARENTAL,
      type: "yes_no",
      text: "Has parent or guardian involvement been documented? (Strongly encouraged for ages 12–15 — not a hard clinical gate)",
      hint: "Document parental or guardian involvement where applicable. This is strongly encouraged for patients aged 12–15 but does not block the pathway if not possible.",
      storesTo: "sharedParentalInvolvement",
      yes: SA_Q3_CONSENT,
      no: SA_Q3_CONSENT,
    },

    // Re-initiation branch — returning patients skip the height/weight/BMI
    // capture entirely and instead confirm the re-initiation attestation
    // criteria below. New patients continue to the standard BMI gate.
    {
      id: SA_REINIT_ROUTE,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) =>
            ctx.context["aht-sel-consultation-type"] === "reinitiation",
          then: SA_REINIT_Q1,
        },
      ],
      default: SA_Q2_BMI_CAPTURE,
    },

    {
      id: SA_REINIT_Q1,
      type: "yes_no",
      text: "Is the patient's original BMI documented and confirmed as having met the eligibility criteria at the time of their initial supply?",
      hint: "The clinician must record the original (initial) BMI. Re-initiation requires documented evidence that the patient met the BMI threshold at first initiation.",
      yes: SA_REINIT_Q2,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    {
      id: SA_REINIT_Q2,
      type: "yes_no",
      text: "Is there documented evidence of at least 5% weight loss achieved on their previous course of this treatment?",
      hint: "A documented treatment response of ≥5% weight loss is required for re-initiation. This must be recorded.",
      yes: SA_REINIT_Q3,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    {
      id: SA_REINIT_Q3,
      type: "yes_no",
      text: "Is the patient presenting within 12 months of their last dose of this treatment?",
      hint: "Re-initiation is only permitted within 12 months of the last dose. If more than 12 months have elapsed, the patient must satisfy the inclusion criteria afresh and be treated as a new initiation — start an Initial Supply consultation instead.",
      yes: SA_REINIT_Q4,
      no: { outcomeId: OUT_REINIT_OVER12M },
    },

    {
      id: SA_REINIT_Q4,
      type: "yes_no",
      text: "Is there documented weight regain since stopping treatment?",
      hint: "Re-initiation requires documented weight regain since the patient stopped treatment.",
      yes: SA_REINIT_BMI_FLOOR,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    // Drug-specific BMI floor check.
    // Source: Mounjaro PGD v1.6 (floor 22 kg/m²),
    //         Wegovy injectable PGD v1.6, Wegovy oral PGD v1.1,
    //         Liraglutide PGD v1.1 (all floor 22.5 kg/m²).
    // The re-initiation drug was stored as aht-sel-reinitiation-drug
    // by the drug-select pathway. BMI is in ctx.context.bmi (captured
    // from vitals before shared assessment runs).
    {
      id: SA_REINIT_BMI_FLOOR,
      type: "condition_route",
      conditions: [
        {
          // Tirzepatide floor: BMI must be ≥22 kg/m²
          evaluate: (ctx) =>
            ctx.context["aht-sel-reinitiation-drug"] === "tirzepatide" &&
            Number(ctx.context.bmi) < 22,
          then: { outcomeId: OUT_REINIT_CANNOT },
        },
        {
          // All other drugs (semaglutide, oral_semaglutide, liraglutide):
          // floor 22.5 kg/m²
          evaluate: (ctx) =>
            ctx.context["aht-sel-reinitiation-drug"] !== "tirzepatide" &&
            Number(ctx.context.bmi) < 22.5,
          then: { outcomeId: OUT_REINIT_CANNOT },
        },
      ],
      // Floor met — proceed to shared monitoring/consent section (same entry
      // point the standard Q2 BMI gate leads to: SA_Q3_CONSENT).
      default: SA_Q3_CONSENT,
    },

    // Combined height + weight + BMI capture
    {
      id: SA_Q2_BMI_CAPTURE,
      type: "bmi_capture",
      text: "Patient Measurements",
      hint: "Record the patient's height and weight. BMI will be calculated automatically.",
      heightConfig: {
        hardMin: 50,
        hardMax: 250,
        decimals: 1,
        plausibilityBands: [
          { min: 120, max: 220, tier: "silent_accept" as const },
          { min: 50, max: 119, tier: "confirmation_prompt" as const, message: "Height below 120 cm is unusual for an adult. Please confirm." },
          { min: 221, max: 250, tier: "confirmation_prompt" as const, message: "Height above 220 cm is unusual. Please confirm." },
        ],
      },
      weightConfig: {
        hardMin: 20,
        hardMax: 350,
        decimals: 1,
        plausibilityBands: [
          { min: 30, max: 300, tier: "silent_accept" as const },
          { min: 20, max: 29, tier: "confirmation_prompt" as const, message: "Weight below 30 kg is unusual for an adult. Please confirm." },
          { min: 301, max: 350, tier: "confirmation_prompt" as const, message: "Weight above 300 kg is unusual. Please confirm." },
        ],
      },
      storesToHeight: "heightCm",
      storesToWeight: "weightKg",
      storesToBmi: "bmi",
      next: SA_Q2_BMI_INCL_ADOLESCENT_NEXT,
    } as any,

    // Routes adolescents to IOTF lookup, adults to BMI threshold confirmation
    {
      id: SA_Q2_BMI_INCL_ADOLESCENT_NEXT,
      type: "condition_route",
      conditions: [
        {
          when: { patientAgeAtLeast: 12, patientAgeUnder: 18 },
          then: SA_Q2_BMI_ADOLESCENT,
        },
      ],
      default: SA_Q2_BMI_CONFIRM,
    },

    // BMI threshold auto-evaluation
    // Evaluates recorded BMI against inclusion thresholds without asking
    // the pharmacist when the answer is unambiguous.
    //
    // Thresholds (AHT drug selection pathway document):
    //   ≥30.0 kg/m²                                          → meets threshold (all groups)
    //   ≥27.5 kg/m² + adjusted ethnic group                  → meets threshold
    //   27.0–29.9 kg/m² without ethnic adjustment            → ambiguous (comorbidity
    //                                                           status unknown at this
    //                                                           stage) — ask pharmacist
    //   <27.0 kg/m²                                          → does not meet threshold
    //
    // Adjusted ethnic groups: south_asian, chinese, other_asian,
    //   middle_eastern, black_african, african_caribbean
    {
      id: SA_Q2_BMI_CONFIRM,
      type: "condition_route",
      conditions: [
        {
          // BMI ≥30 — unambiguously meets threshold for all ethnic groups
          evaluate: (ctx) => Number(ctx.context["bmi"]) >= 30,
          then: SA_Q3_CONSENT,
        },
        {
          // BMI ≥27.5 with ethnic adjustment — meets threshold
          evaluate: (ctx) => {
            const bmi = Number(ctx.context["bmi"]);
            const group = resolveEthnicGroup(ctx.patient);
            return bmi >= 27.5 && ADJUSTED_GROUPS.includes(group ?? "");
          },
          then: SA_Q3_CONSENT,
        },
        {
          // BMI <27 — unambiguously does not meet threshold
          evaluate: (ctx) => Number(ctx.context["bmi"]) < 27,
          then: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
        },
        {
          // BMI 27.0–29.9 without ethnic adjustment — ambiguous.
          // Comorbidity status not yet captured at this point in the shared
          // assessment. Ask pharmacist to confirm.
          evaluate: (ctx) => {
            const bmi = Number(ctx.context["bmi"]);
            const group = resolveEthnicGroup(ctx.patient);
            return bmi >= 27 && bmi < 30 && !ADJUSTED_GROUPS.includes(group ?? "");
          },
          then: SA_Q2_BMI_CONFIRM_MANUAL,
        },
      ],
      default: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
      storesTo: "sharedBmiInclusionMet",
      storedValue: () => true,
    },

    // Manual BMI threshold confirmation — only shown when BMI is 27.0–29.9
    // without ethnic adjustment and comorbidity status is unknown.
    {
      id: SA_Q2_BMI_CONFIRM_MANUAL,
      type: "yes_no",
      text: "BMI is between 27.0–29.9 kg/m². Does the patient have at least one weight-related comorbidity that qualifies them for treatment? (e.g. type 2 diabetes, hypertension, dyslipidaemia, sleep apnoea, cardiovascular disease)",
      hint: "BMI ≥27 with ≥1 weight-related comorbidity meets the inclusion threshold. If no qualifying comorbidity is present, the patient does not currently meet the criteria.",
      storesTo: "sharedBmiInclusionMet",
      yes: SA_Q3_CONSENT,
      no: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Shared commitments (all must be confirmed)
    // MultiSelectNode: routing checks all 4 IDs present → proceed; else → not met
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q3_CONSENT,
      type: "multi_select",
      text: "Confirm the following with the patient before proceeding",
      hint: "All four items must be confirmed. If any cannot be confirmed, the consultation cannot proceed.",
      items: [
        {
          id: "sa-q3-informed-consent",
          text: "Informed consent has been obtained and documented",
        },
        {
          id: "sa-q4-monitoring-consent",
          text: "Patient has consented to monthly monitoring and review",
        },
        {
          id: "sa-q5-lifestyle",
          text: "Patient has agreed to a reduced-calorie diet and increased physical activity alongside treatment",
        },
        {
          id: "sa-q6-f2f",
          text: "Patient is attending face-to-face at a registered GPhC/CQC premises",
        },
      ],
      storesMany: {
        sharedInformedConsent:    (ids: string[]) => ids.includes("sa-q3-informed-consent"),
        sharedMonitoringConsent:  (ids: string[]) => ids.includes("sa-q4-monitoring-consent"),
        sharedLifestyleAgreement: (ids: string[]) => ids.includes("sa-q5-lifestyle"),
        sharedFaceToFace:         (ids: string[]) => ids.includes("sa-q6-f2f"),
      },
      routing: (selections) => {
        const required = [
          "sa-q3-informed-consent",
          "sa-q4-monitoring-consent",
          "sa-q5-lifestyle",
          "sa-q6-f2f",
        ];
        return required.every((id) => selections.includes(id))
          ? SA_Q7_BMI_FLOOR
          : { outcomeId: OUT_COMMITMENTS_NOT_MET };
      },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Shared exclusion criteria (any selected exits)
    // Drug-specific exclusions are NOT assessed here.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q7_BMI_FLOOR,
      type: "checklist",
      text: "Does any of the following apply to this patient?",
      hint: "Select all that apply. If any exclusion criterion is present, the consultation cannot proceed. Select ‘None of the above’ if none apply.",
      items: [
        {
          id: "sa-q8-pregnancy",
          text: "Patient is pregnant, breastfeeding, or planning pregnancy",
        },
        {
          id: "sa-q9-concurrent",
          text: "Patient is currently using another weight loss medicine or GLP-1 receptor agonist for any indication",
        },
        {
          id: "sa-q10-endocrine",
          text: "Obesity is secondary to an endocrine condition currently under specialist management",
        },
        {
          id: "sa-q11-monitoring-refusal",
          text: "Patient is refusing BMI monitoring or required follow-up reviews",
        },
      ],
      includeNoneOption: true,
      noneOptionLabel: "None of the above — no exclusion criteria present",
      storesMany: {
        sharedConcurrentWeightAgent: (ids: string[]) => ids.includes("sa-q9-concurrent"),
        sharedEndocrineObesity:      (ids: string[]) => ids.includes("sa-q10-endocrine"),
        sharedMonitoringRefusal:     (ids: string[]) => ids.includes("sa-q11-monitoring-refusal"),
      },
      anySelected: { outcomeId: OUT_EXCLUSION_TRIGGERED },
      noneSelected: { outcomeId: OUT_CLEARED },
    },
  ],

  outcomes: {
    [OUT_INCLUSION_NOT_MET]: {
      id: OUT_INCLUSION_NOT_MET,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot proceed — inclusion criterion not met",
      reason: "One or more shared inclusion criteria have not been met. Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Discuss with the patient the reason they do not currently meet the criteria for treatment.",
        "Provide lifestyle advice including reduced-calorie diet and increased physical activity.",
        "Refer to GP if clinically appropriate.",
      ],
    },

    [OUT_BMI_BELOW_THRESHOLD]: {
      id: OUT_BMI_BELOW_THRESHOLD,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "BMI below inclusion threshold",
      reason: "The patient's recorded BMI does not meet the minimum threshold for weight management treatment under this service (BMI ≥30; or ≥27 with ≥1 weight-related comorbidity; or ≥27.5 in eligible ethnic groups). Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Discuss the BMI threshold with the patient and explain why treatment cannot be initiated.",
        "Provide lifestyle advice including reduced-calorie diet and increased physical activity.",
        "Refer to GP if the patient has weight-related health concerns that may warrant specialist input.",
        "Advise the patient to return if BMI increases or if weight-related comorbidities develop.",
      ],
    },

    [OUT_BMI_IOTF_BELOW]: {
      id: OUT_BMI_IOTF_BELOW,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "BMI below IOTF threshold for adolescent eligibility",
      reason: "The patient's recorded BMI does not meet the IOTF age- and sex-specific cut-off for adolescent eligibility under this service. Liraglutide in patients aged 12–17 requires BMI at or above the IOTF obesity cut-off for their age and sex.",
      safetyNetting: [
        "Discuss the IOTF BMI threshold with the patient and their parent or guardian.",
        "Refer to GP or paediatric services for weight management support.",
        "Provide age-appropriate lifestyle advice.",
      ],
    },

    [OUT_WEIGHT_BELOW_60KG]: {
      id: OUT_WEIGHT_BELOW_60KG,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Body weight below minimum for adolescent eligibility",
      reason: "Liraglutide in patients aged 12–17 requires body weight above 60 kg. The patient's recorded weight does not meet this criterion. Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Explain to the patient and their parent or guardian that liraglutide requires a minimum body weight of 60 kg.",
        "Refer to GP or paediatric services for weight management support.",
        "Advise the patient to return if body weight increases above 60 kg.",
      ],
    },

    [OUT_CONSENT_REFUSED]: {
      id: OUT_CONSENT_REFUSED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Informed consent not obtained",
      reason: "The patient has not provided informed consent for weight management treatment. Treatment cannot be initiated without consent.",
      safetyNetting: [
        "Document that informed consent was not obtained and the reason where known.",
        "Offer the patient time to consider the treatment and return if they wish to proceed.",
        "Provide written information about the service if the patient would like to review it.",
      ],
    },

    [OUT_MONITORING_REFUSED]: {
      id: OUT_MONITORING_REFUSED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Monitoring and follow-up requirements not accepted",
      reason: "The patient has declined to agree to the monitoring and follow-up requirements of the service. Treatment cannot be safely initiated without the patient's commitment to ongoing review.",
      safetyNetting: [
        "Explain the monitoring requirements to the patient and why they are necessary for safe treatment.",
        "Offer the patient time to consider and return if they agree to the requirements.",
        "Refer to GP if the patient has weight-related health concerns.",
      ],
    },

    [OUT_LIFESTYLE_DECLINED]: {
      id: OUT_LIFESTYLE_DECLINED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Lifestyle commitment not confirmed",
      reason: "The patient has not confirmed commitment to the lifestyle changes required alongside weight management treatment. Pharmacological treatment alone is not appropriate without dietary and physical activity support.",
      safetyNetting: [
        "Explain that weight management medicines work best alongside dietary changes and increased physical activity.",
        "Provide lifestyle advice and signpost to local weight management services.",
        "Invite the patient to return when they are ready to commit to the full programme.",
      ],
    },

    [OUT_COMMITMENTS_NOT_MET]: {
      id: OUT_COMMITMENTS_NOT_MET,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "One or more commitment requirements not confirmed",
      reason: "Not all required commitments were confirmed. Informed consent, monitoring agreement, lifestyle commitment, and face-to-face attendance must all be confirmed before treatment can be initiated.",
      safetyNetting: [
        "Review which commitments could not be confirmed with the patient.",
        "Offer the patient time to consider and return when all commitments can be confirmed.",
        "Refer to GP if the patient has weight-related health concerns.",
      ],
    },

    [OUT_EXCLUSION_TRIGGERED]: {
      id: OUT_EXCLUSION_TRIGGERED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — shared exclusion criterion triggered",
      reason: "A shared exclusion criterion has been identified. Supply cannot proceed under this service.",
      safetyNetting: [
        "Document the exclusion criterion identified.",
        "Advise the patient accordingly.",
        "Refer to GP as appropriate.",
      ],
    },

    [OUT_UNDER12]: {
      id: OUT_UNDER12,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Patient under 12 — not eligible",
      reason: "Weight management medicines under this PGD are not licensed for patients under 12 years of age. Refer to GP or paediatric services.",
      safetyNetting: [
        "Refer to GP or paediatric services for specialist weight management support.",
      ],
    },

    [OUT_OVER84]: {
      id: OUT_OVER84,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Patient aged 85 or over — not eligible",
      reason: "Weight management medicines under this PGD are not licensed for patients aged 85 or over. Refer to GP for specialist review.",
      safetyNetting: [
        "Refer to GP for specialist weight management review.",
      ],
    },

    [OUT_CLEARED]: {
      id: OUT_CLEARED,
      type: "outcome",
      outcomeType: "chain",
      chainId: "aht-wt-shared-assessment-chain",
      title: "Shared assessment cleared",
      reason: "All shared inclusion criteria met and no shared exclusion criteria triggered. Proceed to drug-specific assessment.",
      safetyNetting: [],
    },

    [OUT_REINIT_CANNOT]: {
      id: OUT_REINIT_CANNOT,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Re-initiation criteria not met",
      reason: "One or more re-initiation criteria could not be confirmed. Re-initiation requires: documented original BMI meeting eligibility criteria, documented ≥5% weight loss response, presentation within 12 months of last dose, documented weight regain since stopping, and current BMI above the drug-specific floor.",
      safetyNetting: [
        "Review the patient's treatment records before proceeding.",
        "If the patient's current BMI has fallen below the drug floor, re-initiation cannot proceed under this PGD.",
        "Consider referral to GP for alternative management.",
      ],
    },

    [OUT_REINIT_OVER12M]: {
      id: OUT_REINIT_OVER12M,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Re-initiation not available — more than 12 months since last dose",
      reason: "More than 12 months have elapsed since the patient's last dose. Re-initiation under this PGD is only available within 12 months of the last dose.",
      safetyNetting: [
        "The patient must satisfy the standard eligibility criteria afresh and be treated as a new initiation.",
        "Start a new Initial Supply consultation for this patient.",
      ],
    },
  },
};
